import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { cn } from '@/lib/utils'
import { Combobox } from '@/components/common/Combobox'
import { HaloUserCombobox } from '@/features/halocrm-user/HaloUserCombobox'
import { useVoivodeships } from '@/features/voivodeship/hooks'
import { useCreateCustomer } from '../hooks'
import {
  createCustomerSchema,
  type CreateCustomerFormParsed,
  type CreateCustomerFormValues,
} from '../schema'
import type { CreateCustomerRequest, CustomerType } from '../types'

interface CustomerCreateFormProps {
  defaultType?: CustomerType
  onSuccess?: (createdId: number) => void
  onCancel?: () => void
}

export function CustomerCreateForm({
  defaultType = 'PERSON',
  onSuccess,
  onCancel,
}: CustomerCreateFormProps) {
  const create = useCreateCustomer()
  const { data: voivodeships } = useVoivodeships()
  const voivodeshipOptions = (voivodeships ?? []).map((v) => ({ value: v.code, label: v.labelPl }))

  const form = useForm<CreateCustomerFormValues>({
    resolver: zodResolver(createCustomerSchema),
    defaultValues: {
      type: defaultType,
      firstName: '',
      lastName: '',
      pesel: '',
      name: '',
      nip: '',
      regon: '',
      typeProjectCode: '',
      voivodeshipCode: '',
      assigneeUserId: '',
      note: '',
    },
  })

  const watchType = form.watch('type') as CustomerType

  const switchType = (t: CustomerType) => {
    form.setValue('type', t, { shouldValidate: false, shouldDirty: true })
    // Reset opposite identity fields to keep zod discriminated union clean.
    if (t === 'PERSON') {
      form.setValue('name', '')
      form.setValue('nip', '')
      form.setValue('regon', '')
    } else {
      form.setValue('firstName', '')
      form.setValue('lastName', '')
      form.setValue('pesel', '')
    }
  }

  const onSubmit = async (parsed: CreateCustomerFormParsed) => {
    const payload: CreateCustomerRequest = {
      type: parsed.type,
      firstName: parsed.type === 'PERSON' ? (parsed.firstName || null) : null,
      lastName: parsed.type === 'PERSON' ? (parsed.lastName || null) : null,
      pesel: parsed.type === 'PERSON' ? (parsed.pesel || null) : null,
      name: parsed.type === 'COMPANY' ? (parsed.name || null) : null,
      nip: parsed.type === 'COMPANY' ? (parsed.nip || null) : null,
      regon: parsed.type === 'COMPANY' ? (parsed.regon || null) : null,
      typeProjectCode: parsed.typeProjectCode || null,
      voivodeshipCode: parsed.voivodeshipCode || null,
      assigneeUserId: parsed.assigneeUserId ? Number(parsed.assigneeUserId) : null,
      note: parsed.note || null,
    }
    try {
      const created = await create.mutateAsync(payload)
      toast.success(`Klient utworzony (#${created.id})`)
      form.reset()
      onSuccess?.(created.id)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd zapisu'
      toast.error(`Nie udało się utworzyć klienta: ${msg}`)
    }
  }

  const submitting = create.isPending

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit as never)} className="flex flex-col gap-5">
        <TypeToggle value={watchType} onChange={switchType} />

        {watchType === 'PERSON' ? (
          <>
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Imię *</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="given-name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nazwisko *</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="family-name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pesel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>PESEL</FormLabel>
                  <FormControl>
                    <Input {...field} inputMode="numeric" maxLength={11} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        ) : (
          <>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nazwa firmy *</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="organization" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="nip"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>NIP</FormLabel>
                  <FormControl>
                    <Input {...field} inputMode="numeric" maxLength={10} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="regon"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>REGON</FormLabel>
                  <FormControl>
                    <Input {...field} inputMode="numeric" maxLength={14} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="typeProjectCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Typ projektu</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="PV / POMPA_CIEPLA" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="voivodeshipCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Województwo</FormLabel>
                <FormControl>
                  <Combobox
                    value={field.value || null}
                    onChange={(v) => field.onChange(v ?? '')}
                    options={voivodeshipOptions}
                    emptyOptionLabel="—"
                    placeholder="Wybierz województwo..."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="assigneeUserId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Opiekun</FormLabel>
              <FormControl>
                <HaloUserCombobox
                  value={field.value ? Number(field.value) : null}
                  onChange={(id) => field.onChange(id == null ? '' : String(id))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notatka</FormLabel>
              <FormControl>
                <textarea
                  {...field}
                  rows={3}
                  className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
              Anuluj
            </Button>
          )}
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Zapisywanie...' : 'Utwórz klienta'}
          </Button>
        </div>
      </form>
    </Form>
  )
}

interface TypeToggleProps {
  value: CustomerType
  onChange: (t: CustomerType) => void
}

function TypeToggle({ value, onChange }: TypeToggleProps) {
  return (
    <div className="inline-flex w-fit rounded-md border bg-muted p-0.5">
      <ToggleButton active={value === 'PERSON'} onClick={() => onChange('PERSON')}>
        Osoba fizyczna
      </ToggleButton>
      <ToggleButton active={value === 'COMPANY'} onClick={() => onChange('COMPANY')}>
        Firma
      </ToggleButton>
    </div>
  )
}

function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded px-3 py-1 text-sm transition-colors',
        active ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}
